import React, { useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import './styles/DurationForGoogleTasks.css';

function DurationForGoogleTasks() {
  const posthog = usePostHog();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index, question) => {
    const isOpening = openFaq !== index;
    if (isOpening) {
      posthog?.capture('duration_faq_opened', { index, question });
    }
    setOpenFaq(openFaq === index ? null : index);
  };

  const trackInstallClick = (location) => () => {
    posthog?.capture('duration_install_clicked', { location });
  };

  const trackLegalClick = (label) => () => {
    posthog?.capture('duration_legal_clicked', { label });
  };

  const faqItems = [
    {
      question: "Will it access my personal data?",
      answer: "The extension does not interact with your personal data. You will only be prompted to log in to your Google Account for the to be able to create tasks from any page."
    },
    {
      question: "Does it work on all browsers?",
      answer: "Currently available for Chrome-based browsers. So it would work on Microsoft Edge too, given you install it via the Chrome Web Store. If there is enogh demand, we may consider creating a Firefox version."
    },
    {
      question: "What if I need help?",
      answer: "You can always reach out to us by email at support@xanderj.nl. We are happy to help you with any questions or issues you may have."
    }
  ];

  return (
    <div className="duration-page">
      <section className="hero">
        <div className="hero-content">
          <h1>Power Tools for Google Tasks</h1>
          <p className="subtitle">Visualize your actual day by adding time estimates to Google Tasks</p>
          <p className="hero-testimonial">"Thank you for helping to fill some of the huge gaping holes left by Google!"</p>
          <div className="cta-container">
            <a
              href="https://chromewebstore.google.com/detail/duration-for-google-tasks/pegfblpefnhgggncfepjeknhhapddebh"
              className="cta-button"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackInstallClick('hero')}
            >
              Get The Extension
            </a>
            <div className="trust-badge">
              <span>★★★★★</span>
              <span className="users-count">1,600+ Weekly Active Users</span>
            </div>
          </div>
        </div>
        <div className="hero-image">
          <img src="/images/duration-hero.png" alt="Power Tools for Google Tasks screenshot" />
        </div>
      </section>

      <section className="problem-solution">
        <h2>The Problem with Google Tasks</h2>
        <div className="comparison">
          <div className="before">
            <h3>Without Power Tools for Google Tasks</h3>
            <div className="comparison-image">
              <img src="/images/11.png" alt="Google Tasks without duration" />
            </div>
            <ul className="comparison-list">
              <li><span className="icon-x">✖</span> No way to visualize how long tasks take</li>
              <li><span className="icon-x">✖</span> Calendar view doesn't reflect actual time needed</li>
              <li><span className="icon-x">✖</span> Hard to plan your day realistically</li>
              <li><span className="icon-x">✖</span> Can't prioritize your most important work</li>
            </ul>
          </div>
          <div className="after">
            <h3>With Power Tools for Google Tasks</h3>
            <div className="comparison-image">
              <img src="/images/12.png" alt="Google Tasks with duration" />
            </div>
            <ul className="comparison-list">
              <li><span className="icon-check">✓</span> Clear time estimates for each task</li>
              <li><span className="icon-check">✓</span> Calendar shows accurate task durations</li>
              <li><span className="icon-check">✓</span> Plan your day with confidence</li>
              <li><span className="icon-check">✓</span> Prioritize tasks that matter most</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="features">
        <h2>How It Helps You</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">⏱️</div>
            <h3>Visualize Task Duration</h3>
            <p>Add time estimates to your tasks (like "2h" or "30m") and see them accurately displayed in Calendar</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🌐</div>
            <h3>Create Tasks From Anywhere</h3>
            <p>Right-click on any webpage to quickly create a Google Task without switching tabs</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⭐</div>
            <h3>Set Task Priorities</h3>
            <p>Use customizable priority levels to focus on what's most important each day</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📁</div>
            <h3>Task Templates</h3>
            <p>Save time by creating templates for repetitive tasks and workflows</p>
          </div>
        </div>
      </section>

      <section className="how-it-works">
        <h2>Get Started in 3 Simple Steps</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Install the Extension</h3>
            <p>One-click installation, no account required</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Add Duration to Tasks</h3>
            <p>Simply add (2h) or (30m) to the end of your task titles</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>See Your Day Accurately</h3>
            <p>Tasks in Calendar now show their actual duration</p>
          </div>
        </div>
        <div className="step-cta">
          <a
            href="https://chromewebstore.google.com/detail/duration-for-google-tasks/pegfblpefnhgggncfepjeknhhapddebh"
            className="cta-button"
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackInstallClick('how_it_works')}
          >
            Install Now - It's Free
          </a>
        </div>
      </section>

      <section className="testimonials">
        <h2>What Users Say</h2>
        <div className="testimonial-grid">
          <div className="testimonial-card">
            <div className="testimonial-stars">★★★★★</div>
            <p>"Thank you for helping to fill some of the huge gaping holes left by Google!"</p>
          </div>
          <div className="testimonial-card">
            <div className="testimonial-stars">★★★★★</div>
            <p>"Thanks for the nice work. Simple things like tasks duration and priority levels do so much good to this product!"</p>
          </div>
          <div className="testimonial-card">
            <div className="testimonial-stars">★★★★★</div>
            <p>"Exactly what I was looking for, thanks! Super easy and very helpful."</p>
          </div>
        </div>
      </section>
      
      <section className="faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className={`faq-item ${openFaq === index ? 'active' : ''}`}
              onClick={() => toggleFaq(index, item.question)}
            >
              <div className="faq-question">
                <h3>{item.question}</h3>
                <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
              </div>
              <div className="faq-answer">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to See Your Day More Clearly?</h2>
        <p>Join 1,600+ weekly active users who plan their day accurately with Power Tools for Google Tasks</p>
        <a
          href="https://chromewebstore.google.com/detail/duration-for-google-tasks/pegfblpefnhgggncfepjeknhhapddebh"
          className="cta-button large"
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackInstallClick('footer')}
        >
          Get The Extension
        </a>
        <p className="no-risk">Installs in seconds • Works instantly</p>
      </section>

      <section className="legal-links">
        <h2>Legal Information</h2>
        <p>Please review our policies before using the extension</p>
        <div className="legal-buttons">
          <a
            href="/duration_for_google_tasks/privacy-policy"
            className="legal-button"
            onClick={trackLegalClick('privacy_policy')}
          >
            Privacy Policy
          </a>
          <a
            href="/duration_for_google_tasks/terms-conditions"
            className="legal-button"
            onClick={trackLegalClick('terms_conditions')}
          >
            Terms & Conditions
          </a>
        </div>
      </section>
    </div>
  );
}

export default DurationForGoogleTasks;